import noop from 'lodash/noop'
import { memo, useState, useRef } from 'react'
import { useTranslation } from 'react-i18next'
import { withRouter } from 'react-router'
import styled from 'styled-components'
import { Box, useDialogController, Dialog } from '@machobear/design-system'
import StatusBadge from '@/components/status-badge'
import useRole from '@/hooks/use-role'
import useIsOverflow from '@/hooks/use-is-overflow'
import { colors, fontWeights } from '@/styles'
import config from '@/config'
import { pushToDatalayer } from '@/utils/push-datalayer'
import metadata from './metadata'
import NewLeaseIcon from '../chat-icons/new-lease-icon'
import ShowingDelayedIcon from '../chat-icons/viewing-delayed-icon'
import ShowingIcon from '../chat-icons/viewing-icon'
import BookingDetailsModal from '../../booking-details-modal'

const { APP_PATH } = config

const EventContainer = styled.div`
  display: flex;
  align-items: center;
  justify-content: center;
  background-color: ${props => props.color || colors.secondary};
  width: 80px;
  min-height: 80px;
`

const TitleContainer = styled.div`
  display: flex;
  flex-direction: column;
  justify-content: center;
  background-color: ${colors.white};
  width: calc(100% - 80px);
  padding: 12px;

  ${props =>
    props.isThem
      ? `
      border-radius: 0 8px 8px 0;
      border-left: none;
      padding-right: 16px;
    `
      : `
      border-radius: 8px 0 0 8px;
      border-right: none;
      padding-left: 16px;
    `}
`

const MessageText = styled.p`
  display: flex;
  align-items: center;
  flex-grow: 1;
  margin: 0;
  font-size: 14px;
  line-height: 16px;
  font-weight: ${fontWeights.medium};
  color: ${colors.grey16};
  background-color: ${colors.white};

  ${props =>
    props.clickable &&
    `
    display: block;
    flex-grow: 0;
    margin-bottom: 4px;
  `}
`

const ViewDetailsText = styled.p`
  text-decoration: none;
  font-size: 12px;
  line-height: 16px;
  font-weight: ${fontWeights.medium};
  color: ${colors.link};
  margin: 0;
`

const DescriptionWrapper = styled.div`
  width: 100%;
  overflow: hidden;
`

const DescriptionText = styled.div`
  color: ${colors.grey40};
  white-space: nowrap;
`

const IconImage = styled.img`
  display: flex;
  justify-content: center;
`

const HorizontalRule = styled.div`
  height: 1px;
  background: rgba(0, 0, 0, 0.1);
  margin: 12px 0;
`

const Default = ({ id, event, isThem, history, className }) => {
  const { t } = useTranslation()
  const userRole = useRole()
  const { visible, showModal, closeModal } = useDialogController(false)
  const userType = userRole === 'landlord' ? 'landlord' : 'tenant'
  const data = metadata(isThem, event, t, userRole, history)
  const eventType = event?.event_type
  const eventInfo = event?.event_info || {}
  const DateIcon = eventType === 'chat_contract_extension_started' ? NewLeaseIcon : ShowingIcon
  const linkText = data?.link || t('b.chat.view.view_details.text', 'View details')
  const hasLink = !!data?.to
  const hasOnClick = !!data?.onClick
  const clickable = hasLink || hasOnClick

  const descriptionRef = useRef(null)
  const [description, setDescription] = useState(data?.description)

  useIsOverflow(descriptionRef, () => setDescription(formatDescription(data?.description)))

  const formatDescription = description => {
    return description.substr(0, 12) + '...' + description.substr(-7)
  }

  const renderEventCard = () => {
    if (data?.icon) {
      return (
        <EventContainer color={data?.background}>
          <IconImage src={data?.icon} />
        </EventContainer>
      )
    } else {
      return eventType === 'chat_appointment_delayed' ? (
        <ShowingDelayedIcon info={event?.event_info} color={data?.background} />
      ) : (
        <EventContainer color={data?.background}>
          <DateIcon info={event?.event_info} color={data?.background} />
        </EventContainer>
      )
    }
  }

  return (
    <>
      <Box
        aria-label={description}
        display="flex"
        flexDirection={isThem ? 'row' : 'rowReverse'}
        cursor={clickable ? 'pointer' : 'default'}
        role={hasLink ? 'link' : 'button'}
        tabIndex="0"
        id={id}
        onClick={() => {
          pushToDatalayer({
            event: `${eventType}_click_${userType}`
          })

          if (hasLink) {
            return window.open(`${APP_PATH}${data?.to}`, '_blank')
          }

          if (eventType === 'chat_appointment_user_subscribed') {
            const isTenant = userType === 'tenant'
            return isTenant ? showModal() : data?.onClick(event)
          }

          if (hasOnClick) {
            return data?.onClick(event)
          } else {
            return noop()
          }
        }}
        className={className}
        style={{ width: '264px', outlineOffset: '-3px' }}
      >
        {renderEventCard()}
        <TitleContainer isThem={isThem}>
          <MessageText clickable={clickable}>
            {data?.title || event?.event_info?.message}
          </MessageText>
          {clickable && !data?.hideDefaultLinkText && <ViewDetailsText>{linkText}</ViewDetailsText>}
          {data?.description && (
            <DescriptionWrapper>
              <DescriptionText ref={descriptionRef}>{description}</DescriptionText>
            </DescriptionWrapper>
          )}
          {data?.status && (
            <>
              <HorizontalRule />
              <StatusBadge size="small" space="full" {...data?.status} />
            </>
          )}
        </TitleContainer>
      </Box>
      <Dialog
        id="Showing-booking-modal"
        open={visible}
        onClose={() => closeModal()}
        width="content"
      >
        <BookingDetailsModal
          listingId={event?.listing_id}
          slotData={{
            id: event?.reference_object_id,
            meeting_at: eventInfo.meeting_at,
            last_snooze_time: eventInfo.last_snooze_time,
            duration: eventInfo.duration
          }}
          timelineId={event?.timeline_room}
          closeModal={closeModal}
        />
      </Dialog>
    </>
  )
}

export default withRouter(memo(Default))
